const { find, remove } = require('lodash');

class SavedListsService {
    constructor($rootScope, restangular, $q, $location, bootstrap, analyticsService) {
        this.$rootScope = $rootScope;
        this.restangular = restangular;
        this.api = restangular.one('mylists');
        this.woolworthsRewardsApi = restangular.one('edr');
        this.$q = $q;
        this.$location = $location;
        this.analyticsService = analyticsService;

        // Promise used to cache saved lists
        this.savedListsPromise = null;

        // Used to share new created list details
        this.newListData = null;

        if (bootstrap.isBootstrapped(bootstrap.bootstrapped.SavedLists)) {
            // we do this instead of invalidating on login because sometimes the saved lists service hasn't been
            // instantiated before the login event, meaning we can't call invalidate and the incorrect bootstrapped data
            // will be loaded.
            const bootstrappedSavedLists = bootstrap.getBootstrappedData(bootstrap.bootstrapped.SavedLists);

            if (bootstrappedSavedLists.Response.length > 0) {
                this.savedListsPromise = this.$q.when(this.setSavedLists(bootstrappedSavedLists));
            }
        }
    }

    setSavedLists(data) {
        this.isServiceAvailable = data.IsServiceAvailable;
        this.savedLists = data.Response;
        return this.savedLists;
    }

    invalidateCache() {
        // This will reset the saved lists promise to fetch from the API. This is needed when the
        // result is unpredictable / impractial to determine using the frontend only.
        // This call is slow so we should try to not invalidate the cache if we can, however,
        // Accuracy of data > Speed
        this.savedListsPromise = null;
        return this.getAllSavedLists();
    }

    getIsServiceAvailable() {
        return this.isServiceAvailable;
    }

    getDefaultList() {
        return this.$q.resolve(this.savedListsPromise).then(savedLists => savedLists[0]);
    }

    getAllSavedLists() {
        if (!this.savedListsPromise) {
            // Cache has been invalidated, fetch from API
            this.savedListsPromise = this.api.get().then(data => this.setSavedLists(data));
        }
        return this.savedListsPromise;
    }

    getSmartList(pageNumber, sortType, pageSize) {
        return this.api.one('smart').get({
            PageNumber: pageNumber,
            PageSize: pageSize,
            SortType: sortType,
            Location: this.$location.url(),
            Filters: [],
        });
    }

    getSavedList(listId) {
        // If the cache is valid, just use the list already there
        if (this.savedListsPromise) {
            return this.getAllSavedLists().then(lists => this.$q.when(find(lists, list => list.ListId === listId)));
        }
        // Else fetch only the list specified from the API
        return this.api.one(listId).get();
    }

    setDefaultList(listId) {
        return this.api.one('setdefaultlist', listId).doPOST().then(() => this.invalidateCache());
    }

    saveNewList(name) {
        return this.api.one('add').doPOST({ Name: name }).then((response) => {
            if (!response.Success) {
                throw response;
            }
            this.newListData = response;

            // We need to broadcast when this happens so the product save to list component updates the list
            this.$rootScope.$broadcast('savedLists:AddNewList');

            return this.invalidateCache();
        });
    }

    renameList(listId, newName) {
        return this.api.one('edit').doPOST({ Id: listId, Name: newName }).then(() => {
            const listToRename = find(this.savedLists, list => list.ListId === listId);
            listToRename.Name = newName;

            // We need to broadcast when this happens so the list counts update appropriately
            this.$rootScope.$broadcast('savedLists:RenameList');

            return this.$q.when(this.savedLists);
        }).catch(() => this.invalidateCache());
    }

    deleteList(listId) {
        const isDefault = this.savedLists[0].ListId === listId;
        return this.api.one('deleteList', listId).doPOST().then(() => {
            // If it is the default list, the order changes, so we need to fetch from the API
            if (isDefault) {
                return this.invalidateCache();
            }

            // If it is not the default list, we do not need to refetch the whole list, making this call faster
            remove(this.savedLists, list => list.ListId === listId);
            return this.$q.when(this.savedLists);
        }).catch(() => this.invalidateCache());
    }

    getProductsForSelectedList(listId, pageNumber, sortType, pageSize = 10, filters) {
        // In case something passes undefined
        if (pageSize === undefined) {
            pageSize = 10;
        }
        if (sortType === undefined) {
            sortType = 'Name';
        }
        return this.restangular.one('mylists', listId).post('products', {
            PageNumber: pageNumber,
            PageSize: pageSize,
            SortType: sortType,
            Location: this.$location.url(),
            Filters: filters ? filters.filters : [],
        });
    }

    getProductsForWoolworthsRewards(pageNumber, sortType, pageSize = 10) {
        // In case something passes undefined as pageSize
        if (pageSize === undefined) {
            pageSize = 10;
        }
        return this.woolworthsRewardsApi.doGET('Products', { pageNumber, pageSize, sortType });
    }

    addListItem(listId, stockcode, quantity, source) {
        return this.restangular.one('mylists', listId).one('Items').doPOST({ StockCode: stockcode, Quantity: quantity, Source: source }).then(() => {
            this.analyticsService.pushEvent(this.analyticsService.events.SAVE_TO_LIST);

            // We cannot just increment as you may add an item that is already in the list in which
            // case will throw off the item count as there cannot be duplicates in a saved list
            // this.savedLists has no way of knowing what is in the list. We MUST invalidate the cache.
            this.$q.when(this.invalidateCache()).then(() => {
                // We need to broadcast when this happens so the list counts update appropriately
                this.$rootScope.$broadcast('savedLists:AddListItem', { listId, stockCode: stockcode, quantity });
            });
        });
    }

    deleteListItem(listId, stockcode) {
        return this.api.one('deleteitem').doPOST({ Id: listId, StockCode: stockcode }).then(() => {
            // Decrement the count from the list
            const listToDeleteItemFrom = find(this.savedLists, list => list.ListId === listId);
            listToDeleteItemFrom.ProductsCount--;

            // We need to broadcast when this happens so the list counts update appropriately
            this.$rootScope.$broadcast('savedLists:DeleteListItem', { listId, stockCode: stockcode });

            return this.$q.when(this.savedLists);
        }).catch(() => this.invalidateCache());
    }
}

SavedListsService.$inject = ['$rootScope', 'Restangular', '$q', '$location', 'BootstrapService', 'AnalyticsService'];

module.exports = SavedListsService;



// WEBPACK FOOTER //
// ./src/common/savedlists/saved-lists.service.js